/**
 * Field Visibility Configuration section for /service/settings.
 *
 * Renders two top-level tabs:
 *   1. Job Cards  — Table Columns + Filter Controls toggles
 *   2. Reports    — 7 accordion panels (one per non-reconciliation report)
 *                   each exposing its own Filter Controls + Date Range Presets
 *
 * Admin-only (gated by the consuming page). On Save, posts the ENTIRE
 * field_visibility_config tree to PUT /api/service/config — backend deep-
 * merges with the stored doc so partial updates are safe.
 *
 * Defaults come from shared constants so a freshly-seeded tenant, or a
 * legacy doc, never shows empty toggles.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Save, Eye, EyeOff, Loader2 } from 'lucide-react';
import { DEFAULT_FIELD_VISIBILITY, deepMergeVisibility } from '@/hooks/serviceVisibilityDefaults';
import { broadcastServiceConfigChanged } from '@/hooks/useServiceConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Human-facing labels for every key in the visibility tree
const JOB_CARDS_COLUMN_LABELS = {
  doc_type: 'Status', party_name: 'Party Name', reg_no: 'Reg No', model: 'Model',
  service_head: 'Service Head', job_type: 'Job Type', supervisor: 'Supervisor', technician: 'Technician',
  vehicle_in_date: 'Vehicle In Date', vehicle_out_date: 'Vehicle Out Date',
  chassis_no: 'Chassis No', mobile_no_1: 'Mobile No', booking_no: 'Booking No',
  customer_type: 'Customer Type', customer_voice: "Customer's Voice", kms: 'KMS', color: 'Color',
};

const JOB_CARDS_FILTER_LABELS = {
  reg_no: 'Reg No search', party_name: 'Party Name search', mobile: 'Mobile search',
  supervisor: 'Supervisor filter', technician: 'Technician filter',
  job_type: 'Job Type filter', service_head: 'Service Head filter', status: 'Status filter',
  show_pdi: 'Show PDI toggle', show_delivered: 'Show Delivered toggle',
  sla_filter: 'SLA quick-filter chips', date_range: 'Date range bar',
};

const REPORT_FILTER_LABELS = {
  reg_no: 'Reg No search', party_name: 'Party Name search', mobile: 'Mobile search',
  supervisor: 'Supervisor filter', technician: 'Technician filter',
  job_type: 'Job Type filter', service_head: 'Service Head filter', status: 'Status filter',
  show_pdi: 'Show PDI toggle', show_delivered: 'Show Delivered toggle',
};

const REPORT_PRESET_LABELS = {
  today: 'Today', yesterday: 'Yesterday', wtd: 'Week-to-Date', mtd: 'Month-to-Date',
  qtd: 'Quarter-to-Date', ytd: 'Year-to-Date', custom: 'Custom',
};

const REPORT_PANEL_LABELS = {
  vehicle_inwards: 'Vehicle Inwards',
  vehicle_outwards: 'Vehicle Outwards',
  technician_wise: 'Technician Wise',
  job_status: 'Job Status',
  service_head_report: 'Service Head',
  job_type_report: 'Job Type',
  supervisor_wise: 'Supervisor Wise',
};

const REPORT_PANEL_ORDER = [
  'vehicle_inwards', 'vehicle_outwards', 'technician_wise', 'job_status',
  'service_head_report', 'job_type_report', 'supervisor_wise',
];

export default function FieldVisibilitySection({ isAdmin, initialConfig, onSaved }) {
  const [collapsed, setCollapsed] = React.useState(false);
  const [tab, setTab] = React.useState('job_cards'); // 'job_cards' | 'reports'
  const [fv, setFv] = React.useState(() =>
    deepMergeVisibility(DEFAULT_FIELD_VISIBILITY, initialConfig?.field_visibility_config)
  );
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [expandedReports, setExpandedReports] = React.useState({ vehicle_inwards: true });

  // Reset local draft if the parent re-fetches the config
  React.useEffect(() => {
    setFv(deepMergeVisibility(DEFAULT_FIELD_VISIBILITY, initialConfig?.field_visibility_config));
    setDirty(false);
  }, [initialConfig]);

  const toggle = (path) => {
    if (!isAdmin) return;
    setFv((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      let node = clone;
      for (let i = 0; i < path.length - 1; i++) node = node[path[i]];
      const leaf = path[path.length - 1];
      node[leaf] = !node[leaf];
      return clone;
    });
    setDirty(true);
  };

  const bulkSet = (dictPath, value) => {
    if (!isAdmin) return;
    setFv((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      let node = clone;
      for (const k of dictPath) node = node[k];
      for (const leaf of Object.keys(node)) node[leaf] = value;
      return clone;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/service/config`,
        { field_visibility_config: fv },
        { headers: authHeaders() }
      );
      toast.success('Field visibility saved');
      setDirty(false);
      broadcastServiceConfigChanged();
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFv(deepMergeVisibility(DEFAULT_FIELD_VISIBILITY, initialConfig?.field_visibility_config));
    setDirty(false);
  };

  const CountChip = ({ on, total }) => (
    <span className={`ml-2 inline-flex items-center text-[11px] px-1.5 py-0.5 rounded-md font-medium ${
      on === total ? 'bg-emerald-50 text-emerald-700' : on === 0 ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-700'
    }`}>{on}/{total} visible</span>
  );

  const countOn = (obj) => Object.values(obj).filter(Boolean).length;

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="field-visibility-section">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full px-5 py-4 border-b border-slate-100 flex items-center gap-3 text-left hover:bg-slate-50/60"
        data-testid="field-visibility-toggle"
      >
        <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
          {collapsed ? <EyeOff className="w-5 h-5 text-sky-600" /> : <Eye className="w-5 h-5 text-sky-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-[16px] font-semibold text-slate-800 leading-tight">Field Visibility Configuration</h2>
          <p className="text-[12.5px] text-slate-500">Show or hide columns, filters, and date presets across Service Job Cards and Reports. Changes apply tenant-wide.</p>
        </div>
        {collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {!collapsed && (
        <div className="px-5 py-5 space-y-4" data-testid="field-visibility-body">
          {/* Tabs */}
          <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1 w-fit">
            {[
              { id: 'job_cards', label: 'Job Cards' },
              { id: 'reports', label: 'Reports' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`fv-tab-${t.id}`}
                className={`px-4 py-1.5 rounded-lg text-[12.5px] font-medium transition-all ${
                  tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'job_cards' && (
            <div className="space-y-5" data-testid="fv-job-cards-panel">
              <SubGroup
                title="Table Columns"
                subtitle="Note: Job No, Job Hours and Days Delay are always shown."
                labels={JOB_CARDS_COLUMN_LABELS}
                values={fv.job_cards.columns}
                onToggle={(k) => toggle(['job_cards', 'columns', k])}
                onBulk={(v) => bulkSet(['job_cards', 'columns'], v)}
                testIdPrefix="fv-jc-col"
                isAdmin={isAdmin}
              />
              <SubGroup
                title="Filter Controls"
                labels={JOB_CARDS_FILTER_LABELS}
                values={fv.job_cards.filters}
                onToggle={(k) => toggle(['job_cards', 'filters', k])}
                onBulk={(v) => bulkSet(['job_cards', 'filters'], v)}
                testIdPrefix="fv-jc-filter"
                isAdmin={isAdmin}
              />
            </div>
          )}

          {tab === 'reports' && (
            <div className="space-y-2" data-testid="fv-reports-panel">
              {REPORT_PANEL_ORDER.map((rk) => {
                const expanded = !!expandedReports[rk];
                const report = fv.reports[rk];
                return (
                  <div key={rk} className="rounded-xl border border-slate-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedReports((s) => ({ ...s, [rk]: !s[rk] }))}
                      className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-100 flex items-center justify-between"
                      data-testid={`fv-report-header-${rk}`}
                    >
                      <span className="text-[13.5px] font-medium text-slate-800 flex items-center">
                        {REPORT_PANEL_LABELS[rk]}
                        <CountChip on={countOn(report.filters) + countOn(report.date_presets)} total={Object.keys(report.filters).length + Object.keys(report.date_presets).length} />
                      </span>
                      {expanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                    </button>
                    {expanded && (
                      <div className="px-4 py-4 space-y-5 bg-white" data-testid={`fv-report-body-${rk}`}>
                        <SubGroup
                          title="Filter Controls"
                          labels={REPORT_FILTER_LABELS}
                          values={report.filters}
                          onToggle={(k) => toggle(['reports', rk, 'filters', k])}
                          onBulk={(v) => bulkSet(['reports', rk, 'filters'], v)}
                          testIdPrefix={`fv-rep-${rk}-filter`}
                          isAdmin={isAdmin}
                          compact
                        />
                        <SubGroup
                          title="Date Range Presets"
                          subtitle="Tomorrow and Last 7 Days are always available."
                          labels={REPORT_PRESET_LABELS}
                          values={report.date_presets}
                          onToggle={(k) => toggle(['reports', rk, 'date_presets', k])}
                          onBulk={(v) => bulkSet(['reports', rk, 'date_presets'], v)}
                          testIdPrefix={`fv-rep-${rk}-preset`}
                          isAdmin={isAdmin}
                          compact
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Save bar */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={handleReset}
              disabled={!dirty || !isAdmin}
              data-testid="fv-reset-btn"
              className="px-3.5 py-2 rounded-xl text-[13px] text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving || !isAdmin}
              data-testid="fv-save-btn"
              className="px-4 py-2 rounded-xl text-[13px] font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Visibility'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SubGroup({ title, subtitle, labels, values, onToggle, onBulk, testIdPrefix, isAdmin, compact }) {
  const total = Object.keys(labels).length;
  const on = Object.keys(labels).filter((k) => values[k]).length;
  return (
    <div data-testid={`${testIdPrefix}-group`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-[13.5px] font-semibold text-slate-800">{title}</h4>
          {subtitle && <p className="text-[11.5px] text-slate-500">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400">{on}/{total} on</span>
          <button
            type="button"
            onClick={() => onBulk(true)}
            disabled={!isAdmin}
            data-testid={`${testIdPrefix}-all-on`}
            className="text-[11.5px] text-emerald-700 hover:underline disabled:opacity-40"
          >All on</button>
          <span className="text-slate-300">·</span>
          <button
            type="button"
            onClick={() => onBulk(false)}
            disabled={!isAdmin}
            data-testid={`${testIdPrefix}-all-off`}
            className="text-[11.5px] text-rose-600 hover:underline disabled:opacity-40"
          >All off</button>
        </div>
      </div>
      <div className={`grid ${compact ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'} gap-2`}>
        {Object.entries(labels).map(([k, label]) => (
          <label
            key={k}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors ${
              values[k] ? 'border-indigo-200 bg-indigo-50/40 hover:bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50'
            } ${!isAdmin ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              checked={!!values[k]}
              disabled={!isAdmin}
              onChange={() => onToggle(k)}
              data-testid={`${testIdPrefix}-${k}`}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-[12.5px] text-slate-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
